/**
 * Ljudmätning för jaktens transkribering. Röstdetektering: tysta 15-sekundersbitar
 * skickas inte till Whisper. Det sparar 70–90 % av transkriberingskostnaden och
 * stoppar Whispers påhittade fraser ("Tack till elever …") vid tystnad.
 *
 * Fail-open: kunde nivån inte mätas (iOS kan pausa AudioContext) skickas biten
 * ändå, så det blir aldrig sämre än utan detektering.
 */

/** Minsta tid med röstnivå i en bit för att den ska skickas. */
export const MIN_VOICED_MS = 300;
/** Lägsta RMS som räknas som ljud över huvud taget (0–1, float-PCM). */
const ABS_MIN_RMS = 0.006; // lågt nog för viskning nära telefonen
/** Röst måste ligga så här många gånger över bakgrundsbruset. */
const NOISE_FACTOR = 3;

export type CycleStats = { voicedMs: number; measured: boolean };

/** Ren logik: följer bakgrundsbruset och räknar tid med röstnivå. */
export class VoiceActivityCounter {
  private noiseFloor = ABS_MIN_RMS / NOISE_FACTOR;
  private voicedMs = 0;
  private measured = false;

  /** Lägg till en mätning (RMS för ett intervall på frameMs millisekunder). */
  push(rms: number, frameMs: number): void {
    if (rms > 0) this.measured = true;
    const threshold = Math.max(ABS_MIN_RMS, this.noiseFloor * NOISE_FACTOR);
    if (rms > threshold) {
      this.voicedMs += frameMs;
      // Bruset kryper bara långsamt uppåt under tal (t.ex. stigande vind).
      this.noiseFloor += (rms - this.noiseFloor) * 0.002;
    } else {
      // Under tystnad följer brusnivån snabbare, både upp och ner.
      this.noiseFloor += (rms - this.noiseFloor) * 0.05;
    }
  }

  /** Statistik för biten som just avslutades; nollställer inför nästa. */
  takeCycle(): CycleStats {
    const stats = { voicedMs: this.voicedMs, measured: this.measured };
    this.voicedMs = 0;
    this.measured = false;
    return stats;
  }
}

/**
 * Skottdetektering: ett skott nära telefonen överstyr mikrofonen — en stor
 * del av samplingarna ligger nära max — och kommer plötsligt (RMS hoppar
 * många gånger över föregående mätning). Även tal som klipps når inte upp i
 * samma andel, och tal byggs upp gradvis. Skott på långt håll som inte
 * överstyr mikrofonen registreras inte (hellre missat än falskt skott).
 */
/** Nivå (0–1) som räknas som "nära max". */
export const SHOT_NEAR_MAX = 0.9;
/** Andel av fönstrets samplingar som måste ligga nära max. */
const SHOT_MIN_NEAR_MAX_FRACTION = 0.1;
const SHOT_MIN_RMS = 0.2;
const SHOT_ONSET_FACTOR = 6;
/** Ekon och efterföljande skott inom den här tiden räknas som samma händelse. */
export const SHOT_REFRACTORY_MS = 3000;

export class ShotDetector {
  private prevRms = 0;
  private lastShotAt = -Infinity;

  /** Returnerar true när mätningen ser ut som ett skott. */
  push(m: { rms: number; nearMaxFraction: number }, nowMs: number): boolean {
    const onset = m.rms >= Math.max(SHOT_MIN_RMS, this.prevRms * SHOT_ONSET_FACTOR);
    this.prevRms = m.rms;
    if (!onset || m.nearMaxFraction < SHOT_MIN_NEAR_MAX_FRACTION) return false;
    if (nowMs - this.lastShotAt < SHOT_REFRACTORY_MS) return false;
    this.lastShotAt = nowMs;
    return true;
  }
}

export function shouldSendChunk(stats: CycleStats): boolean {
  if (!stats.measured) return true; // fail-open
  return stats.voicedMs >= MIN_VOICED_MS;
}

const FRAME_MS = 40;

/**
 * Mäter mikrofonnivån på strömmen var 40:e ms (nästan heltäckande med 2048
 * samplingar per fönster), för röstdetektering och skottdetektering.
 */
export function startVoiceActivityMeter(
  stream: MediaStream,
  opts: { onShot?: () => void } = {},
): { takeCycle(): CycleStats; stop(): void } | null {
  const AudioCtx = window.AudioContext || (window as any).webkitAudioContext;
  if (!AudioCtx) return null;
  try {
    const ctx: AudioContext = new AudioCtx();
    const source = ctx.createMediaStreamSource(stream);
    const analyser = ctx.createAnalyser();
    analyser.fftSize = 2048;
    source.connect(analyser);
    const buf = new Float32Array(analyser.fftSize);
    const counter = new VoiceActivityCounter();
    const shots = new ShotDetector();
    const timer = window.setInterval(() => {
      if (ctx.state !== 'running') { ctx.resume().catch(() => {}); return; }
      analyser.getFloatTimeDomainData(buf);
      let sum = 0;
      let nearMax = 0;
      for (let i = 0; i < buf.length; i++) {
        const v = buf[i];
        sum += v * v;
        if (v >= SHOT_NEAR_MAX || v <= -SHOT_NEAR_MAX) nearMax++;
      }
      const rms = Math.sqrt(sum / buf.length);
      counter.push(rms, FRAME_MS);
      if (opts.onShot && shots.push({ rms, nearMaxFraction: nearMax / buf.length }, Date.now())) opts.onShot();
    }, FRAME_MS);
    return {
      takeCycle: () => counter.takeCycle(),
      stop: () => {
        window.clearInterval(timer);
        try { source.disconnect(); } catch {}
        ctx.close().catch(() => {});
      },
    };
  } catch (err) {
    console.warn('[QVENARI-TRANSCRIPT] Röstdetektering kunde inte startas — skickar alla bitar:', err);
    return null;
  }
}
